import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { useApplicationModals } from "app/common/hooks";
import {
  useCollector,
  usePaginatedCollector,
} from "app/integration/common/hooks";
import {
  IconButton,
  ServerSidePaginationButtons,
} from "app/components/atoms/button";
import { SearchInput } from "app/components/atoms/input";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "app/components/atoms/badge";
import * as XLSX from 'xlsx';

const breadcrumbItems = [
  { label: "Scrap", href: "#" },
];

function Scrap() {
  const navigate = useNavigate();
  const { openModal, setPayloads } = useApplicationModals();

  const [params, setParams] = useState({
    search: "",
    stock_opname_search: "",
    status: null,
    customer_id: null,
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    collect(params);
  }, [params]);

  const [
    scraps,
    setScrap,
    scrapLoader,
    collect,
    scrapPaginationProps,
  ] = usePaginatedCollector({ modelName: "scrap", params: params });

  const [productVariants] = useCollector({ modelName: "productVariant" });

  const [expandedRows, setExpandedRows] = useState({});

  const exportLaporanExcel = () => {
    // Prepare data for Excel
    const dataForExcel = scraps.flatMap(scrap => {
      // If there are product codes, create a row for each code
      if (scrap?.product_code_list?.length > 0) {
        return scrap.product_code_list.map(code => ({
          'Produk': productVariants?.find(item => item.id === scrap?.product_variant_id)?.product_detail?.name || '-',
          'Varian': `${productVariants?.find(item => item.id === scrap?.product_variant_id)?.color?.name || ''}, ${productVariants?.find(item => item.id === scrap?.product_variant_id)?.size?.name || ''}`,
          'Kode Produk': code.qr_code || '-',
          'Alasan': code.reason || scrap?.reason || '-',
          'Keterangan': code.description || scrap?.description || '-',
          'Jumlah': 1, // Each row represents one product code
          'Status': scrap?.status || '-',
          'Tanggal': scrap?.createdAt ? new Date(scrap.createdAt).toLocaleString('id-ID') : '-'
        }));
      }
      // If no product codes, create a single row with basic info
      return {
        'Produk': productVariants?.find(item => item.id === scrap?.product_variant_id)?.product_detail?.name || '-',
        'Varian': `${productVariants?.find(item => item.id === scrap?.product_variant_id)?.color?.name || ''}, ${productVariants?.find(item => item.id === scrap?.product_variant_id)?.size?.name || ''}`,
        'Kode Produk': '-',
        'Alasan': scrap?.reason || '-',
        'Keterangan': scrap?.description || '-',
        'Jumlah': scrap?.qty || 0,
        'Status': scrap?.status || '-',
        'Tanggal': scrap?.createdAt ? new Date(scrap.createdAt).toLocaleString('id-ID') : '-'
      };
    });
  
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: ['Produk', 'Varian', 'Kode Produk', 'Alasan', 'Keterangan', 'Jumlah', 'Status', 'Tanggal']
    });
  
    // Auto-size columns
    const columnWidths = [
      { wch: 30 }, // Produk
      { wch: 20 }, // Varian
      { wch: 20 }, // Kode Produk
      { wch: 20 }, // Alasan
      { wch: 30 }, // Keterangan
      { wch: 10 }, // Jumlah
      { wch: 15 }, // Status
      { wch: 20 }  // Tanggal
    ];
    worksheet['!cols'] = columnWidths;
  
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Scrap");
  
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
  
    // Create download link
    const blob = new Blob([excelBuffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    // Set filename with current date
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    link.download = `laporan_scrap_${formattedDate}.xlsx`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="warehouse"
      activeSidebarChild="scrap"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Scrap"
      headingButtons={[
        {
          label: "Tambah Scrap",
          type: "routerButton",
          to: "/scrap/create",
        },
        {
          label: "Download Laporan",
          type: "button",
          onClick: () => {
            openModal("exportScrapExcel");
          },
        },
      ]}
    >
      <AppModals
        items={["scrap.create", "exportScrapExcel"]}
        onSuccess={{
          "scrap.create": () => collect(),
          "exportScrapExcel": () => collect(),
        }}
      />
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <Table.SearchBarItemWrapper>
              <SearchInput testId="scrap-search-input"
                placeholder="Cari Scrap"
                type="text"
                value={params.search}
                onChange={(e) =>
                  setParams({ ...params, search: e.target.value })
                }
              />
            </Table.SearchBarItemWrapper>
          </Table.SearchBarWrapper>
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading></Table.Heading>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Produk Variant</Table.Heading>
              <Table.Heading>Jumlah</Table.Heading>
              <Table.Heading>Alasan</Table.Heading>
              <Table.Heading>Deskripsi</Table.Heading>
              <Table.Heading>Tanggal</Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading></Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {scraps?.map((scrap, key) => (
              <Fragment key={key}>
                <Table.BodyRow>
                  <Table.Cell>
                    {scrap?.product_code_list?.length > 0 && (
                      <IconButton testId="scrap-tampilkan-catatan-button"
                      icon={expandedRows[key] ? MinusCircleIcon : PlusCircleIcon}
                      onClick={() => setExpandedRows(prev => ({
                        ...prev,
                        [key]: !prev[key]
                      }))}
                      title={expandedRows[key] ? "Sembunyikan Catatan" : "Tampilkan Catatan"}
                      className="text-gray-500 hover:text-blue-500"
                      data-tip={expandedRows[key] ? "Sembunyikan Catatan" : "Tampilkan Catatan"}
                      data-for="note-tooltip"
                    />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center">
                      {productVariants?.find((item) => item.id === scrap?.product_variant_id)?.product_detail?.name}
                    </div>
                  </Table.Cell>
                  <Table.Cell>{productVariants?.find((item) => item.id === scrap?.product_variant_id)?.color?.name}, {productVariants?.find((item) => item.id === scrap?.product_variant_id)?.size?.name}</Table.Cell>
                  <Table.Cell>
                    {parseInt(scrap?.qty)}
                  </Table.Cell>
                  <Table.Cell>
                    {scrap?.reason}
                  </Table.Cell>
                  <Table.Cell>
                    {scrap?.description}
                  </Table.Cell>
                  <Table.Cell>
                    <Typography.Date date={scrap?.createdAt} format="dd MMMM yyyy hh:mm:ss" />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={scrap?.status === "pending" ? "yellow" : scrap?.status === "approved" ? "green" : "red"}>{scrap?.status}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1 items-center">
                      {/* Action buttons */}
                    </div>
                  </Table.Cell>
                </Table.BodyRow>

                {expandedRows[key] && scrap?.product_code_list?.length > 0 && (
                  <Table.BodyRow className="bg-gray-50">
                    <Table.Cell colSpan="8">
                      <div className="px-4 py-3">
                        <div className="flex items-center mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Daftar Kode Produk</h4>
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                            {scrap.product_code_list.length} item
                          </span>
                        </div>
                        <div className="space-y-2">
                          {scrap.product_code_list.map((code, idx) => (
                            <div key={idx} className="group bg-white rounded-lg border border-gray-100 hover:border-blue-100 transition-colors duration-150 shadow-sm hover:shadow">
                              <div className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <div className="bg-gray-50 px-3 py-1.5 rounded-md border border-gray-100">
                                        <span className="font-mono text-sm font-medium text-gray-800">{code.qr_code}</span>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <Badge 
                                          color={
                                            code.reason? 'red' : 
                                            'gray'
                                          }
                                          className="px-2 py-1 text-xs font-medium"
                                        >
                                          {code.scrap_reason || 'Tidak ada alasan'}
                                        </Badge>
                                      </div>
                                    </div>
                                    {code.description && (
                                      <p className="mt-2 text-sm text-gray-600">
                                        <span className="font-medium">Catatan: </span>
                                        {code.description}
                                      </p>
                                    )}
                                  </div>
                                  <div className="ml-2 flex-shrink-0 flex items-center">
                                    <span className="text-xs text-gray-400">
                                      {new Date(code.createdAt || new Date()).toLocaleDateString('id-ID', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Table.Cell>
                  </Table.BodyRow>
                )}
              </Fragment>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={scrapPaginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default Scrap;
